import { useCallback, useRef, useEffect } from 'react';

interface UseDebouncedHoverOptions<T = void> {
  onShow?: (data?: T) => void;
  onHide?: () => void;
  delay?: number;
}

interface UseDebouncedHoverReturn<T = void> {
  handleMouseEnter: (data?: T) => void;
  handleMouseLeave: () => void;
  clearTimeout: () => void;
}

const DEFAULT_HOVER_DELAY = 150;

export function useDebouncedHover(options: UseDebouncedHoverOptions): {
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  clearTimeout: () => void;
};

export function useDebouncedHover<T>(
  options: UseDebouncedHoverOptions<T>,
): UseDebouncedHoverReturn<T>;

export function useDebouncedHover<T = void>({
  onShow,
  onHide,
  delay = DEFAULT_HOVER_DELAY,
}: UseDebouncedHoverOptions<T>): UseDebouncedHoverReturn<T> {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHoverTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(
    (data?: T) => {
      clearHoverTimeout();
      onShow?.(data);
    },
    [clearHoverTimeout, onShow],
  );

  const handleMouseLeave = useCallback(() => {
    clearHoverTimeout();
    timeoutRef.current = setTimeout(() => {
      onHide?.();
    }, delay);
  }, [clearHoverTimeout, onHide, delay]);

  useEffect(() => {
    return () => {
      clearHoverTimeout();
    };
  }, [clearHoverTimeout]);

  return {
    handleMouseEnter,
    handleMouseLeave,
    clearTimeout: clearHoverTimeout,
  };
}
